/**
 * A minimal reusable CRUD table component for simple Admin pages
 * (Teams, AI Tools, Phases, Survey Roles, Questions).
 */
import { useState } from 'react'
import ConfirmModal from './ConfirmModal'

export default function CrudTable({ items, columns, onCreate, onUpdate, onDelete, newItemFields }) {
  const [editId, setEditId]       = useState(null)
  const [editData, setEditData]   = useState({})
  const [newData, setNewData]     = useState(() =>
    Object.fromEntries(newItemFields.map((f) => [f.key, f.default ?? '']))
  )
  const [error, setError]           = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null) // item id pending deletion

  const handleCreate = async () => {
    try {
      await onCreate(newData)
      setNewData(Object.fromEntries(newItemFields.map((f) => [f.key, f.default ?? ''])))
      setError(null)
    } catch (e) { setError(e.response?.data?.detail || 'Create failed') }
  }

  const startEdit = (item) => { setEditId(item.id); setEditData({ ...item }) }

  const handleUpdate = async () => {
    try { await onUpdate(editId, editData); setEditId(null); setError(null) }
    catch (e) { setError(e.response?.data?.detail || 'Update failed') }
  }

  const doDelete = async () => {
    const id = confirmDelete
    setConfirmDelete(null)
    try { await onDelete(id); setError(null) }
    catch (e) { setError(e.response?.data?.detail || 'Delete failed') }
  }

  return (
    <div>
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Add new row */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {newItemFields.map((f) => (
          <div key={f.key} className="filter-group">
            <label>{f.label}</label>
            <input
              type={f.type || 'text'}
              className="form-control"
              style={{ minWidth: 140 }}
              value={newData[f.key]}
              onChange={(e) => setNewData((d) => ({ ...d, [f.key]: e.target.value }))}
            />
          </div>
        ))}
        <button type="button" className="btn btn-primary" onClick={handleCreate}>
          + Add
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((c) => <th key={c.key}>{c.label}</th>)}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  style={{ textAlign: 'center', padding: '2rem 1rem', color: 'var(--text-secondary)' }}
                >
                  No items yet. Use the form above to add one.
                </td>
              </tr>
            )}
            {items.map((item) => (
              <tr key={item.id}>
                {columns.map((c) => (
                  <td key={c.key}>
                    {editId === item.id && c.editable ? (
                      c.type === 'boolean' ? (
                        <select
                          className="form-control"
                          style={{ width: 'auto', minWidth: 80 }}
                          value={editData[c.key] ?? 1}
                          onChange={(e) => setEditData((d) => ({ ...d, [c.key]: Number(e.target.value) }))}
                        >
                          <option value={1}>Yes</option>
                          <option value={0}>No</option>
                        </select>
                      ) : (
                        <input
                          className="form-control"
                          style={{ width: 'auto', minWidth: 120 }}
                          value={editData[c.key] ?? ''}
                          onChange={(e) => setEditData((d) => ({ ...d, [c.key]: e.target.value }))}
                        />
                      )
                    ) : (
                      c.type === 'boolean' ? (
                        <span style={{ color: item[c.key] ? 'var(--success-color)' : 'var(--text-secondary)' }}>
                          {item[c.key] ? 'Yes' : 'No'}
                        </span>
                      ) : (
                        String(item[c.key] ?? '')
                      )
                    )}
                  </td>
                ))}
                <td>
                  {editId === item.id ? (
                    <div className="inline-edit-controls">
                      <button type="button" className="btn btn-primary btn-sm" onClick={handleUpdate}>Save</button>
                      <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="inline-edit-controls">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => startEdit(item)}>Edit</button>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(item.id)}>Delete</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={confirmDelete !== null}
        title="Delete item"
        message="This action cannot be undone. Are you sure you want to delete this item?"
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
